import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/IcSummaryModal.css';

/**
 * Admin modal: send a monthly payment summary to a selected Payment IC via
 * WhatsApp. Lets the admin pick which IC, previews the exact message (tutees +
 * amounts + total), then confirms to send.
 */
const IcSummaryModal = ({ isOpen, onClose, month, year, paymentICs = [] }) => {
  const [selectedIc, setSelectedIc] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedIc('');
      setPreview(null);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  useEffect(() => {
    const loadPreview = async () => {
      if (!selectedIc) { setPreview(null); return; }
      setLoadingPreview(true);
      setError('');
      try {
        const res = await adminApi.previewIcPaymentSummary(month, year, selectedIc);
        setPreview(res.data?.data || null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load preview');
        setPreview(null);
      } finally {
        setLoadingPreview(false);
      }
    };
    loadPreview();
  }, [selectedIc, month, year]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!selectedIc) { setError('Please select a Payment IC.'); return; }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminApi.sendIcPaymentSummary(month, year, selectedIc);
      setSuccess(res.data?.message || 'Summary sent.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send summary.');
    } finally {
      setSending(false);
    }
  };

  const monthName = new Date(2000, month - 1).toLocaleString('en-US', { month: 'long' });

  return (
    <div className="ic-summary-overlay" onClick={onClose}>
      <div className="ic-summary-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ic-summary-header">
          <h2>Send Payment Summary via WhatsApp</h2>
          <button className="ic-summary-close" onClick={onClose}>×</button>
        </div>

        <div className="ic-summary-body">
          <p className="ic-summary-period">{monthName} {year}</p>

          <label className="ic-summary-label">Payment IC</label>
          <select
            className="ic-summary-select"
            value={selectedIc}
            onChange={(e) => setSelectedIc(e.target.value)}
            disabled={sending}
          >
            <option value="">Select a Payment IC…</option>
            {paymentICs.map(ic => (
              <option key={ic._id} value={ic._id}>
                {ic.paynowName} — {ic.paynowNumber}
              </option>
            ))}
          </select>

          {loadingPreview && <p className="ic-summary-muted">Loading preview…</p>}

          {preview && !loadingPreview && (
            <div className="ic-summary-preview">
              <div className="ic-summary-preview-head">
                Preview — sends to {preview.icPhone || '(no WhatsApp number!)'} · {preview.invoiceCount} invoice(s)
              </div>
              {preview.invoiceCount === 0 ? (
                <p className="ic-summary-muted">No finalized invoices for this IC this period.</p>
              ) : (
                <pre className="ic-summary-text">{preview.text}</pre>
              )}
            </div>
          )}

          {error && <div className="ic-summary-error">{error}</div>}
          {success && <div className="ic-summary-success">{success}</div>}
        </div>

        <div className="ic-summary-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Close</button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || !selectedIc || !preview || preview.invoiceCount === 0 || !preview.icPhone}
          >
            {sending ? 'Sending…' : 'Send via WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IcSummaryModal;
